import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { useRecruitmentUsers } from '../useRecruitmentUsers';
import {
  isJobPostingDraftComplete,
  sampleJobPostingDraft,
  toJobPostingRequest,
  type JobPostingDraft,
} from '../utils/jobPostingDraft';
import { vacanciesApi } from '../vacanciesApi';
import type { VacancyCycle } from '../vacancyTypes';
import { JobPostingFields } from './JobPostingFields';

/**
 * Corrects the LIVE posting's Job Posting stage — the Job Posting card's Edit. The same three fields
 * the tag form asks, through the same component. A tag written before the fields existed opens with
 * the poster empty, which is the truth, and the primary stays disabled until somebody chooses one.
 */
export function JobPostingModal({
  itemId,
  cycle,
  onClose,
  onSaved,
}: {
  itemId: string;
  cycle: VacancyCycle;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const accounts = useRecruitmentUsers();

  const [draft, setDraft] = useState<JobPostingDraft>({
    postedByUserId: cycle.postedByUserId ?? null,
    postedDate: cycle.postedDate ?? '',
    remarks: cycle.remarks ?? '',
  });
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const canSave = isJobPostingDraftComplete(draft) && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await vacanciesApi.updatePosting(itemId, toJobPostingRequest(draft));
      toast.success('Job posting updated.');
      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the job posting.', 0);
      setError(failure);
      focusValidationError(failure, { idPrefix: 'job-posting' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Edit job posting"
      size="lg"
      id="job-posting-modal"
      autogenerate={{ onGenerate: () => setDraft(sampleJobPostingDraft(accounts.users)), disabled: saving || accounts.loading }}
      confirm={{ id: 'job-posting-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id="job-posting-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="job-posting-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <JobPostingFields
          idPrefix="job-posting"
          draft={draft}
          onChange={setDraft}
          users={accounts.users}
          usersLoading={accounts.loading}
          usersError={accounts.error}
          disabled={saving}
          error={error}
        />
      </div>
    </Modal>
  );
}
